import { useEffect, useReducer } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import { MyBenefitsListPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { BenefitModalContext } from 'modules/benefits/components/benefitModals/BenefitModalContext'
import { benefitModalReducer } from 'modules/benefits/components/benefitModals/reducer'
import type { BenefitSetupStatus } from 'modules/benefits/lib/benefitListings'
import { EmployeeBenefitsList } from 'modules/benefits/pages/MyBenefits/MyBenefitsList/EmployeeBenefitsList'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const MyBenefitsListPage = () => {
  const location = useLocation()
  const isSubRoute = location.pathname.includes('/perks')

  useEffect(() => {
    track(trackingEvents.benefits.openMyBenefitsListPage)
  }, [])

  const [modalState, modalDispatch] = useReducer(benefitModalReducer, {
    openModal: null,
  })
  const {
    company: { id: companyId },
    employee,
  } = useCurrentSession()

  const employeeId = employee?.id
  const { data } = useQuery(MyBenefitsListPageDocument, {
    variables: {
      companyId,
      employeeId: employeeId || 0,
    },
  })

  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes

  const enableEnrollment =
    featureAvailabilities?.some(
      (feature) => feature.id === Features.benefits_healthcare && feature.available
    ) && (data?.benefits?.healthcareEnrollmentsConnection?.edges?.length ?? 0) === 0 //Add more conditions when we have dependants

  const perksEnabled =
    featureAvailabilities?.some(
      (feature) => feature.id === Features.benefits_perks && feature.available
    ) ?? false

  function healthcareEnrollment(): BenefitSetupStatus | undefined {
    if (!data) return undefined

    const edges = data.benefits?.healthcareEnrollmentsConnection?.edges
    if (!edges) return undefined

    const enrollments = edges.map((edge) => edge.node)

    if (enrollments.length === 0) {
      return 'pending_enrollment'
    }

    // Map all nodes and identify their statuses (resolves status of most-active covered member)
    const statuses = enrollments.map((enrollment) => enrollment.status)

    if (statuses.includes('active')) {
      return 'active'
    }

    if (statuses.includes('requested')) {
      return 'pending_activation'
    }

    return 'pending_enrollment'
  }

  // If we're on a sub-route, render the outlet instead of the benefits list
  if (isSubRoute) {
    return <Outlet context={{ featureAvailabilities, perksEnabled }} />
  }

  return (
    <ResponsivePage.Body showHeader={false} variant="full">
      <SectionHeader
        title={i18n.t('benefits.my_benefits.my_benefits_list.title')}
        description={i18n.t('benefits.my_benefits.my_benefits_list.description')}
      />

      <Box paddingTop="s24" />

      <BenefitModalContext.Provider value={{ state: modalState, dispatch: modalDispatch }}>
        <EmployeeBenefitsList
          healthcarePlans={data?.benefits?.healthcarePlansConnection?.nodes}
          healthcareEnrollmentStatus={healthcareEnrollment()}
          featureAvailabilities={featureAvailabilities}
          enableEnrollment={!!enableEnrollment}
        />
      </BenefitModalContext.Provider>
    </ResponsivePage.Body>
  )
}

export default MyBenefitsListPage
